import { AtSign, Bot, ListChecks, Sparkles, ShieldAlert, Cloud, Cog, Network, Link2, PlugZap, Radar, DatabaseBackup, MonitorSmartphone, Receipt, Server, ShieldCheck, Ticket, Users, Webhook, type LucideIcon } from 'lucide-react';
import { ROUTES, psaProjectsView, docsView, rmmAlertsView, rmmDevicesView, cippDevicesView, cspLicencesView, backupSaasView, insightsCoverageView, insightsGapsView, networkView, domainsView, socView } from '@/lib/routes';
import { FEATURES } from '@/lib/features';

/**
 * Every live module's navigation, in one place: the left sidebar (`components/shell/Sidebar`),
 * the Home landing page and the header page search all render these, so a page added here
 * appears everywhere. CLAUDE.md: keep this in step with every page/module change.
 *
 * Shape: module → groups → items. A link whose `to` carries a query string is a pre-filtered
 * **view** of its page (the page applies it on arrival, then drops it — see rmmDevicesView). Views
 * are not in the sidebar: they appear in the page's own "Views" dropdown (`ViewsMenu`, rendered by
 * PageHeader), and in Home and the page search.
 */

export type NavItem =
  | { kind: 'link'; to: string; label: string; hint?: string }
  /** A planned page — shown muted with a "Soon" pill so the roadmap is visible without a dead link. */
  | { kind: 'soon'; label: string; hint?: string };

export type NavLinkItem = Extract<NavItem, { kind: 'link' }>;

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const isNavLink = (i: NavItem): i is NavLinkItem => i.kind === 'link';
/** A pre-filtered view (query string) rather than a page. */
export const isView = (i: NavLinkItem) => i.to.includes('?');

// PSA, RMM and CIPP dashboards live in the Dashboards section (DASHBOARDS_MENU), not here.
export const PSA_MENU: NavGroup[] = [
  { title: 'Tickets', items: [
    { kind: 'link', to: ROUTES.psa.tickets.mine,   label: 'My Tickets' },
    { kind: 'link', to: ROUTES.psa.tickets.queues, label: 'Queues' },
    { kind: 'link', to: ROUTES.psa.tickets.search, label: 'Search Tickets' },
    { kind: 'link', to: ROUTES.psa.tickets.new,    label: 'New Ticket' },
  ] },
  { title: 'Dispatch', items: [
    { kind: 'link', to: ROUTES.psa.dispatch, label: 'Dispatch board', hint: 'Drop tickets and project tasks on Outlook calendars' },
  ] },
  { title: 'Opportunities', items: [
    { kind: 'link', to: ROUTES.psa.opportunities.all,    label: 'Opportunities' },
    { kind: 'link', to: ROUTES.psa.opportunities.mine,   label: 'My Opportunities' },
    { kind: 'link', to: ROUTES.psa.opportunities.search, label: 'Search Opportunities' },
    { kind: 'link', to: ROUTES.psa.opportunities.new,    label: 'New Opportunity' },
  ] },
  { title: 'Projects', items: [
    { kind: 'link', to: ROUTES.psa.projects.all,     label: 'Projects', hint: 'Every live Autotask project with progress, hours and task health' },
    { kind: 'link', to: ROUTES.psa.projects.mine,    label: 'My projects', hint: 'Projects you lead or have tasks on' },
    { kind: 'link', to: ROUTES.psa.projects.myTasks, label: 'My tasks', hint: 'Your open project tasks, with fast time logging' },
    { kind: 'link', to: psaProjectsView({ show: 'pastEnd' }),    label: 'Past end date' },
    { kind: 'link', to: psaProjectsView({ show: 'stale' }),      label: 'No recent activity' },
    { kind: 'link', to: psaProjectsView({ show: 'ready' }),      label: 'Ready for Pending Completion' },
    { kind: 'link', to: psaProjectsView({ show: 'unassigned' }), label: 'Has unassigned tasks' },
    { kind: 'soon', label: 'Timeline (all projects)' },
    { kind: 'soon', label: 'New project from template' },
  ] },
];

export const RMM_MENU: NavGroup[] = [
  { title: 'Devices', items: [
    { kind: 'link', to: ROUTES.rmm.devices,                          label: 'All devices' },
    { kind: 'link', to: ROUTES.rmm.sites,                            label: 'By site' },
    { kind: 'link', to: rmmDevicesView({ status: 'offline30' }),     label: 'Not seen 30+ days' },
    { kind: 'link', to: rmmDevicesView({ reboot: '1' }),             label: 'Reboot required' },
    { kind: 'link', to: rmmDevicesView({ remote: 'missing' }),       label: 'No ScreenConnect' },
  ] },
  { title: 'Alerts', items: [
    { kind: 'link', to: ROUTES.rmm.alerts,                           label: 'Open alerts' },
    { kind: 'link', to: rmmAlertsView({ priority: 'Critical,High' }), label: 'Critical & High' },
    { kind: 'link', to: rmmAlertsView({ ticket: 'with' }),           label: 'With a ticket' },
  ] },
  { title: 'Remote access', items: [
    { kind: 'link', to: ROUTES.rmm.remote,                           label: 'ScreenConnect sessions' },
    { kind: 'link', to: `${ROUTES.rmm.remote}?show=no-rmm`,          label: 'Not in Datto RMM' },
    { kind: 'link', to: `${ROUTES.rmm.remote}?show=unmapped`,        label: 'No client' },
    { kind: 'link', to: `${ROUTES.rmm.remote}?show=orphan`,          label: 'Dead agents' },
  ] },
];

export const CIPP_MENU: NavGroup[] = [
  { title: 'Microsoft 365', items: [
    { kind: 'link', to: ROUTES.cipp.tenants, label: 'Tenants' },
    { kind: 'soon', label: 'Licences' },
  ] },
  { title: 'Intune devices', items: [
    { kind: 'link', to: ROUTES.cipp.devices,                         label: 'All devices' },
    { kind: 'link', to: cippDevicesView({ rmm: 'missing' }),         label: 'Not in Datto RMM' },
    { kind: 'link', to: cippDevicesView({ compliance: 'noncompliant' }), label: 'Non-compliant' },
  ] },
];

export const CSP_MENU: NavGroup[] = [
  { title: 'Renewals', items: [
    { kind: 'link', to: ROUTES.csp.dashboard,                     label: 'Dashboard' },
    { kind: 'link', to: ROUTES.csp.customers,                     label: 'Customers' },
    { kind: 'link', to: ROUTES.csp.licences,                      label: 'All licences' },
    { kind: 'link', to: cspLicencesView({ status: 'expiring' }),  label: 'Expiring soon' },
  ] },
  { title: 'Ordering', items: [
    { kind: 'link', to: ROUTES.csp.products, label: 'Products' },
    { kind: 'link', to: ROUTES.csp.requests, label: 'Requests' },
  ] },
  { title: 'Data', items: [
    { kind: 'link', to: ROUTES.csp.upload,   label: 'Upload report' },
    { kind: 'link', to: ROUTES.csp.mappings, label: 'Mappings' },
  ] },
];

export const BACKUP_MENU: NavGroup[] = [
  { title: 'SaaS Protection', items: [
    { kind: 'link', to: ROUTES.backup.saas,                        label: 'Customers' },
    { kind: 'link', to: backupSaasView({ show: 'attention' }),     label: 'Needs attention' },
    { kind: 'link', to: backupSaasView({ show: 'gaps' }),          label: 'Users not protected' },
    { kind: 'link', to: backupSaasView({ show: 'waste' }),         label: 'Archived & billed' },
  ] },
  { title: 'BCDR', items: [
    { kind: 'soon', label: 'Devices & agents' },
    { kind: 'soon', label: 'Endpoint Backup' },
  ] },
];

/**
 * The Dashboards section: custom dashboards, then every module's built-in dashboard grouped by
 * module (decided 2026-10-14). PSA / RMM / CIPP dashboards live ONLY here; Sophos, UniFi and CSP
 * renewals are also their modules' landing pages, so they're linked from both. The sidebar adds
 * the user's starred dashboards beneath (per user, from the API). URLs stay under each module.
 */
export const DASHBOARDS_MENU: NavGroup[] = [
  { title: 'Custom', items: [
    { kind: 'link', to: ROUTES.dashboards,                  label: 'All dashboards' },
  ] },
  { title: 'PSA', items: [
    { kind: 'link', to: ROUTES.psa.dashboards.serviceDesk,  label: 'Service desk' },
    { kind: 'link', to: ROUTES.psa.dashboards.sales,        label: 'Sales' },
  ] },
  { title: 'RMM', items: [
    { kind: 'link', to: ROUTES.rmm.dashboards.overview,     label: 'RMM overview' },
    { kind: 'link', to: ROUTES.rmm.dashboards.patching,     label: 'Patching' },
  ] },
  { title: 'Microsoft 365', items: [
    { kind: 'link', to: ROUTES.cipp.dashboards.security,    label: 'Security posture' },
  ] },
  { title: 'Other modules', items: [
    { kind: 'link', to: ROUTES.soc.root,                    label: 'SOC (Huntress)' },
    { kind: 'link', to: ROUTES.network.sophos.dashboard,    label: 'Sophos firewalls' },
    { kind: 'link', to: ROUTES.network.unifi.dashboard,     label: 'UniFi' },
    { kind: 'link', to: ROUTES.csp.dashboard,               label: 'NCE renewals' },
  ] },
];

/** Built-in dashboard paths that belong to the Dashboards section, not their module's. */
const DASHBOARD_PREFIXES = [ROUTES.psa.dashboards.root, '/rmm/dashboards', '/cipp/dashboards'];

export const INSIGHTS_MENU: NavGroup[] = [
  { title: 'Find', items: [
    { kind: 'link', to: ROUTES.insights.find,                                      label: 'Find a device or person' },
  ] },
  { title: 'Coverage', items: [
    { kind: 'link', to: ROUTES.insights.coverage,                                  label: 'Device coverage' },
    { kind: 'link', to: insightsCoverageView({ state: 'gaps' }),                   label: 'Coverage gaps' },
    { kind: 'link', to: insightsCoverageView({ state: 'drift' }),                  label: 'Check-in drift' },
    { kind: 'link', to: insightsCoverageView({ state: 'duplicates' }),             label: 'Duplicates' },
  ] },
  { title: 'Gaps', items: [
    { kind: 'link', to: ROUTES.insights.gaps,                                      label: 'Gap list & tickets' },
    { kind: 'link', to: insightsGapsView({ severity: 'critical' }),                label: 'Critical gaps' },
    { kind: 'link', to: insightsGapsView({ kind: 'no-backup' }),                   label: 'Users not backed up' },
  ] },
  { title: 'Clients', items: [
    { kind: 'link', to: ROUTES.companies,                                          label: 'All companies' },
    { kind: 'link', to: `${ROUTES.companies}?show=unmapped`,                       label: 'Mapping health' },
  ] },
  { title: 'Coming next', items: [
    { kind: 'soon', label: 'Alerts inbox' },
    { kind: 'soon', label: 'QBR pack' },
  ] },
];

/** AI: chat with the Foundry agent (server/agent.ts). One page for now. */
export const AI_MENU: NavGroup[] = [
  { title: 'Assistant', items: [
    { kind: 'link', to: ROUTES.ai, label: 'Chat' },
  ] },
];

/** SOC (Huntress): flat pages under /soc. Views use socView(path, { show }). */
export const SOC_MENU: NavGroup[] = [
  { title: 'Huntress', items: [
    { kind: 'link', to: ROUTES.soc.root,                                          label: 'Overview' },
    { kind: 'link', to: ROUTES.soc.incidents,                                     label: 'Incidents' },
    { kind: 'link', to: socView(ROUTES.soc.incidents, { show: 'open' }),          label: 'Open incidents' },
    { kind: 'link', to: ROUTES.soc.agents,                                        label: 'Agents' },
    { kind: 'link', to: socView(ROUTES.soc.agents, { show: 'attention' }),        label: 'Agents needing attention' },
    { kind: 'link', to: socView(ROUTES.soc.agents, { show: 'stale' }),            label: 'Stale agents' },
    { kind: 'link', to: ROUTES.soc.identities,                                    label: 'Identities (ITDR)' },
    { kind: 'link', to: ROUTES.soc.organizations,                                 label: 'Organisations' },
  ] },
  { title: 'Gaps', items: [
    { kind: 'link', to: insightsGapsView({ kind: 'no-edr' }),                     label: 'Computers without Huntress' },
  ] },
];

/** Sophos and UniFi are separate groups — their data is never combined. */
export const NETWORK_MENU: NavGroup[] = [
  { title: 'Sophos', items: [
    { kind: 'link', to: ROUTES.network.sophos.dashboard,                                        label: 'Sophos dashboard' },
    { kind: 'link', to: ROUTES.network.sophos.firewalls,                                        label: 'Firewalls' },
    { kind: 'link', to: ROUTES.network.sophos.subscriptions,                                    label: 'Subscriptions' },
    { kind: 'link', to: ROUTES.network.sophos.alerts,                                           label: 'Alerts' },
    { kind: 'link', to: ROUTES.network.sophos.tenants,                                          label: 'Tenants' },
    { kind: 'link', to: networkView(ROUTES.network.sophos.firewalls, { show: 'disconnected' }), label: 'Disconnected firewalls' },
    { kind: 'link', to: networkView(ROUTES.network.sophos.firewalls, { show: 'subscription' }), label: 'Xstream expiring' },
  ] },
  { title: 'UniFi', items: [
    { kind: 'link', to: ROUTES.network.unifi.dashboard,                                    label: 'UniFi dashboard' },
    { kind: 'link', to: ROUTES.network.unifi.sites,                                        label: 'Sites' },
    { kind: 'link', to: ROUTES.network.unifi.consoles,                                     label: 'Consoles' },
    { kind: 'link', to: ROUTES.network.unifi.devices,                                      label: 'Devices' },
    { kind: 'link', to: ROUTES.network.unifi.wan,                                          label: 'ISP & WAN' },
    { kind: 'link', to: networkView(ROUTES.network.unifi.devices, { show: 'offline' }),    label: 'Offline devices' },
    { kind: 'link', to: networkView(ROUTES.network.unifi.devices, { show: 'updates' }),    label: 'Updates available' },
  ] },
];

export const DOMAINS_MENU: NavGroup[] = [
  { title: 'Domains', items: [
    { kind: 'link', to: ROUTES.domains.root,                       label: 'All domains' },
    { kind: 'link', to: domainsView({ show: 'ready' }),            label: 'Ready to tighten' },
    { kind: 'link', to: domainsView({ show: 'problems' }),         label: 'Failing senders / issues' },
    { kind: 'link', to: domainsView({ show: 'missing' }),          label: 'No DMARC' },
  ] },
  { title: 'Coverage', items: [
    { kind: 'link', to: domainsView({ show: 'gaps' }),             label: 'Missing from a system' },
    { kind: 'link', to: domainsView({ show: 'gap-dmarcian' }),     label: 'dmarcian required (Premium / Enterprise)' },
    { kind: 'link', to: domainsView({ show: 'gap-itglue' }),       label: 'Not in IT Glue' },
    { kind: 'link', to: domainsView({ show: 'monitored' }),        label: 'In dmarcian' },
    { kind: 'link', to: domainsView({ show: 'unmonitored' }),      label: 'Not in dmarcian' },
    { kind: 'link', to: domainsView({ show: 'unmapped' }),         label: 'Unmapped domains' },
  ] },
];

export const DOCS_MENU: NavGroup[] = [
  { title: 'IT Glue', items: [
    { kind: 'link', to: ROUTES.docs.organizations,                                         label: 'Organisations' },
    { kind: 'link', to: ROUTES.docs.configurations,                                        label: 'Configurations' },
    { kind: 'link', to: ROUTES.docs.expirations,                                           label: 'Expirations' },
  ] },
  { title: 'Hygiene', items: [
    { kind: 'link', to: docsView(ROUTES.docs.expirations, { show: 'soon' }),               label: 'Expiring in 90 days' },
    { kind: 'link', to: docsView(ROUTES.docs.configurations, { show: 'no-rmm' }),          label: 'Not in Datto RMM' },
    { kind: 'link', to: docsView(ROUTES.docs.configurations, { show: 'rmm-gone' }),        label: 'Datto device gone' },
    { kind: 'link', to: docsView(ROUTES.docs.organizations, { show: 'unlinked' }),         label: 'Orgs not linked to Autotask' },
  ] },
];

export type ModuleId = 'dashboards' | 'ai' | 'soc' | 'insights' | 'psa' | 'rmm' | 'cipp' | 'csp' | 'backup' | 'network' | 'domains' | 'docs';

/** Menus by module id (see `MODULES` in lib/modules.ts). */
export const MODULE_MENUS: Partial<Record<ModuleId, NavGroup[]>> = {
  domains: DOMAINS_MENU,
  docs: DOCS_MENU,
  network: NETWORK_MENU,
  dashboards: DASHBOARDS_MENU,
  ...(FEATURES.aiChat ? { ai: AI_MENU } : {}),
  soc: SOC_MENU,
  insights: INSIGHTS_MENU,
  psa: PSA_MENU,
  rmm: RMM_MENU,
  cipp: CIPP_MENU,
  csp: CSP_MENU,
  backup: BACKUP_MENU,
};

/** First path segment → module id. Companies (Orbit) belong to Insights. */
const SEGMENT_TO_MODULE: Record<string, ModuleId> = {
  dashboards: 'dashboards', ai: 'ai', soc: 'soc', psa: 'psa', rmm: 'rmm', cipp: 'cipp', csp: 'csp', backup: 'backup', network: 'network', domains: 'domains', docs: 'docs', insights: 'insights', companies: 'insights',
};

export function moduleForPath(pathname: string): ModuleId | null {
  if (DASHBOARD_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return 'dashboards';
  return SEGMENT_TO_MODULE[pathname.split('/')[1] ?? ''] ?? null;
}

/**
 * The page link the current path belongs to: the most specific plain (no query) link matching
 * it, so /rmm/devices/<uid> lights "All devices" and /psa/tickets/mine lights "My Tickets".
 * Views (query links) never count as current — their params are stripped on arrival.
 */
export function activeLinkFor(pathname: string, groups: NavGroup[]): NavLinkItem | null {
  let active: NavLinkItem | null = null;
  for (const g of groups) for (const i of g.items) {
    if (!isNavLink(i) || isView(i)) continue;
    if ((pathname === i.to || pathname.startsWith(`${i.to}/`)) && (!active || i.to.length > active.to.length)) active = i;
  }
  return active;
}

/** Pre-filtered views of exactly this page (its path + a query string), in menu order. */
export function viewsForPath(pathname: string): NavLinkItem[] {
  const out: NavLinkItem[] = [];
  for (const groups of Object.values(MODULE_MENUS)) for (const g of groups ?? []) for (const i of g.items) {
    if (isNavLink(i) && isView(i) && i.to.split('?')[0] === pathname && !out.some((o) => o.to === i.to)) out.push(i);
  }
  return out;
}

// -------- Client scope (header picker) --------

/**
 * Pages that narrow to the client picked in the header (ClientScopeContext). Each one opts in via
 * its list hook's `scoped` option; this list is what the UI uses to say so (PageHeader shows "not
 * filtered by client" on menu pages that are NOT here). Keep it in step when scoping a page.
 */
const SCOPED_PATHS = new Set<string>([
  ROUTES.psa.tickets.mine, ROUTES.psa.tickets.queues, ROUTES.psa.tickets.search,
  ROUTES.psa.opportunities.all, ROUTES.psa.opportunities.mine, ROUTES.psa.opportunities.search, ROUTES.psa.search,
  ROUTES.psa.projects.all, ROUTES.psa.projects.mine,
  ROUTES.rmm.devices, ROUTES.rmm.alerts, ROUTES.rmm.sites, ROUTES.rmm.remote, ROUTES.rmm.dashboards.overview, ROUTES.rmm.dashboards.patching,
  ROUTES.cipp.tenants, ROUTES.cipp.devices, ROUTES.cipp.dashboards.security,
  ROUTES.csp.dashboard, ROUTES.csp.customers, ROUTES.csp.licences,
  ROUTES.backup.saas,
  ROUTES.domains.root,
  ROUTES.docs.organizations, ROUTES.docs.configurations, ROUTES.docs.expirations,
  ROUTES.insights.coverage, ROUTES.insights.gaps,
]);
/** Whole areas that are scoped (every Sophos / UniFi page). */
const SCOPED_PREFIXES = [ROUTES.network.root, ROUTES.soc.root];

export function isScopeAware(pathname: string): boolean {
  return SCOPED_PATHS.has(pathname) || SCOPED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** A page that's a plain (non-view) menu link — i.e. a list/dashboard page, not a record page. */
export function isMenuPage(pathname: string): boolean {
  return Object.values(MODULE_MENUS).some((groups) => groups?.some((g) => g.items.some((i) => isNavLink(i) && !isView(i) && i.to === pathname)));
}

// -------- Admin --------

export interface AdminLink { to: string; label: string; icon: LucideIcon }
export interface AdminSection { title: string; icon: LucideIcon; items: AdminLink[] }

/**
 * Admin, organised by module (rendered as the Admin section of the sidebar, admins only). Each module gets its own section with a Settings page (driven by
 * the `server/moduleSettings.ts` registry) plus any module-specific admin pages. AI assistants sit
 * under the module they serve (Engineering + Sales are Autotask-based, so PSA for now).
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  { title: 'General', icon: ShieldCheck, items: [
    { to: ROUTES.admin.users,  label: 'Users',  icon: Users },
    { to: ROUTES.admin.system, label: 'System', icon: Server },
    { to: ROUTES.admin.integrations, label: 'Integrations', icon: PlugZap },
    { to: ROUTES.admin.mappings, label: 'Client mappings', icon: Link2 },
  ] },
  { title: 'Insights', icon: Radar, items: [
    { to: ROUTES.admin.insights, label: 'Settings', icon: Cog },
  ] },
  { title: 'PSA · Autotask', icon: Ticket, items: [
    { to: ROUTES.admin.psa,         label: 'Settings',     icon: Cog },
    { to: ROUTES.admin.aiAssistant, label: 'AI Assistant', icon: Bot },
    { to: ROUTES.admin.projectStatus, label: 'Project status log', icon: ListChecks },
    { to: ROUTES.admin.webhook,     label: 'Webhook',      icon: Webhook },
  ] },
  { title: 'RMM · Datto RMM', icon: MonitorSmartphone, items: [
    { to: ROUTES.admin.rmm, label: 'Settings', icon: Cog },
  ] },
  { title: 'CIPP · Microsoft 365', icon: Cloud, items: [
    { to: ROUTES.admin.cippSettings, label: 'Settings',       icon: Cog },
    { to: ROUTES.admin.cipp,         label: 'Tenant mapping', icon: Cloud },
  ] },
  { title: 'CSP · Microsoft NCE', icon: Receipt, items: [
    { to: ROUTES.admin.csp, label: 'Settings', icon: Cog },
  ] },
  { title: 'Network · Sophos & UniFi', icon: Network, items: [
    { to: ROUTES.admin.networkSettings, label: 'Settings', icon: Cog },
    { to: ROUTES.admin.network,         label: 'Mapping',  icon: Link2 },
  ] },
  { title: 'Domains · dmarcian', icon: AtSign, items: [
    { to: ROUTES.admin.domains, label: 'Settings', icon: Cog },
  ] },
  { title: 'Backup · Datto SaaS', icon: DatabaseBackup, items: [
    { to: ROUTES.admin.backup, label: 'Settings', icon: Cog },
  ] },
  { title: 'SOC · Huntress', icon: ShieldAlert, items: [
    { to: ROUTES.admin.soc, label: 'Settings', icon: Cog },
  ] },
  // Parked (lib/features.ts): no Admin → AI section while the agent chat is off.
  ...(FEATURES.aiChat ? [{ title: 'AI · Foundry agent', icon: Sparkles, items: [
    { to: ROUTES.admin.ai, label: 'Settings', icon: Cog },
  ] }] : []),
];
